import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import leftRotation from "../../assets/left_rotation.svg";
import rightRotation from "../../assets/right_rotation.svg";

import circle from "../../assets/circle.svg";
import rectangle from "../../assets/rectangle.svg";

import reset from "../../assets/reset.svg";
import download from "../../assets/download.svg";

import "./ImageCropper.scss";
import { CroppedImageSize } from "../../types";

interface EditorBarProps {
  cropper: Cropper;
  croppedImageSize?: CroppedImageSize;
}

const EditorBar = memo(
  forwardRef(({ cropper, croppedImageSize }: EditorBarProps, ref) => {
    const barRef = useRef<HTMLImageElement>(null);
    const [isCircleCropper, setIsCircleCropper] = useState(
      !!croppedImageSize?.isCircleCropper,
    );

    const [croppeAspectRatio, setCroppeAspectRatio] = useState(
      croppedImageSize?.croppeAspectRatio
        ? croppedImageSize.croppeAspectRatio
        : NaN,
    );

    const aspectRatio = useMemo(
      () => [
        {
          label: "Original",
          value: NaN,
          style: {
            height: "24px",
            width: "31px",
          },
        },
        {
          label: "1:1",
          value: 1,
          style: {
            height: "24px",
            width: "24px",
          },
        },
        {
          label: "16:9",
          value: 16 / 9,
          style: {
            height: "26px",
            width: "37px",
          },
        },

        {
          label: "9:16",
          value: 9 / 16,
          style: {
            height: " 37px",
            width: "26px",
          },
        },
        {
          label: "4:3",
          value: 4 / 3,
          style: {
            height: "24px",
            width: "30px",
          },
        },
        {
          label: "4:5",
          value: 4 / 5,
          style: {
            height: "30px",
            width: "24px"
          },
        },
        {
          label: "3:4",
          value: 3 / 4,
          style: {
            height: "28px",
            width: "24px"
          },
        },
        {
          label: "2:3",
          value: 2 / 3,
          style: {
            height: "29px",
            width: "24px",
          },
        },
      ],
      [],
    );
    const changeAspectRatio = (item: any) => {
      const data = cropper.getData();

      cropper.setAspectRatio(item.value);

      cropper.setData(data);

      setCroppeAspectRatio(item.value);
    };
    // 获取圆形canvas
    const getRoundedCanvas = (sourceCanvas: HTMLCanvasElement) => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) {
        return;
      }
      const width = sourceCanvas.width;
      const height = sourceCanvas.height;
      canvas.width = width;
      canvas.height = height;

      context.imageSmoothingEnabled = true;
      context.clearRect(0, 0, width, height);

      context.beginPath();
      context.arc(
        width / 2,
        height / 2,
        Math.min(width, height) / 2,
        0,
        Math.PI * 2,
      );
      context.closePath();
      context.clip();
      context.drawImage(sourceCanvas, 0, 0, width, height);

      return canvas;
    };

    const getCroppedCanvas = useCallback(() => {
      const rectangleShape = cropper.getCroppedCanvas();
      const croppedCanvas = !isCircleCropper
        ? rectangleShape
        : getRoundedCanvas(rectangleShape);

      return croppedCanvas;
    }, [cropper, isCircleCropper]);

    const downloadImage = useCallback(() => {
      const croppedCanvas = getCroppedCanvas();
      if (!croppedCanvas) {
        return;
      }

      croppedCanvas.toBlob((blob) => {
        if (!blob) {
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = "cropped_image.png";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      });
    }, [getCroppedCanvas]);

    const clickRectangleCropper = useCallback(() => {
      const data = cropper.getData();

      cropper.setAspectRatio(NaN);
      const cropBox = document.querySelector(".cropper-crop-box");
      if (!cropBox) {
        return;
      }
      const existingOverlay = cropBox.querySelector(".circle-overlay");
      if (existingOverlay) {
        cropBox.removeChild(existingOverlay);
      }
      cropper.setData(data);
      setCroppeAspectRatio(NaN);
      setIsCircleCropper(false);
    }, [cropper]);

    const appendCircleChild = useCallback(() => {
      const cropBox = document.querySelector(".cropper-crop-box");
      const existingOverlay = document.querySelector(".circle-overlay");
      if (cropBox && !existingOverlay) {
        const circleOverlay = document.createElement("div");
        circleOverlay.className = "circle-overlay";
        circleOverlay.style.cssText =
          "position: absolute; top: 50%; left: 50%; width: 100%; height: 100%; border-radius: 50%; border: 1.5px dashed #2089FB; transform: translate(-50%, -50%); pointer-events: none;";
        cropBox.appendChild(circleOverlay);
      }
    }, []);

    const clickCircleCropper = useCallback(() => {
      const data = cropper.getData();

      cropper.setAspectRatio(1);
      appendCircleChild();
      cropper.setData(data);
      setCroppeAspectRatio(1);
      setIsCircleCropper(true);
    }, [appendCircleChild, cropper]);

    useEffect(() => {
      if (!barRef.current) {
        return;
      }
      const parentWidth = barRef.current.parentElement?.offsetWidth || 0;
      barRef.current.style.width = '160px'//`${(parentWidth / 3) * 2}px`;
      if (croppedImageSize) {
        cropper.setData(croppedImageSize);
      }
      if (isCircleCropper) {
        clickCircleCropper();
      }

      return () => {};
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getCroppedCanvas,
        isCircleCropper,
        croppeAspectRatio,
      }),
      [croppeAspectRatio, getCroppedCanvas, isCircleCropper],
    );
    return (
      <>

        <div className="aspect-ratio font-size-12" style={{display:isCircleCropper?'none':'flex',justifyContent:'space-between'}}>
          {(
            <>
              {aspectRatio.map((item) => {
                return (
                    <div key={item.label} className='flex-column-center cursor-pointer' onClick={() => changeAspectRatio(item)}>
                        <div style={{height:'40px',display:'flex',alignItems:'flex-end'}}>
                            <div
                                key={item.value}
                                className={`aspect-box ${
                                    croppeAspectRatio === item.value ||
                                    (isNaN(croppeAspectRatio) && isNaN(item.value))
                                        ? "aspect-box-active"
                                        : ""
                                }`}
                                // @ts-ignore
                                style={{...item.style, lineHeight: item.style.height}}>
                            </div>
                        </div>

                        <span>{item.label}</span>
                    </div>

                );
              })}
            </>
          )}
        </div>

          <div className="operate-bar font-size-12" ref={barRef}>
              {/*<img*/}
              {/*  className="operate"*/}
              {/*  src={leftRotation}*/}
              {/*  alt="leftRotation"*/}
              {/*  onClick={() => cropper.rotate(-45)}*/}
              {/*/>*/}
              {/*<img*/}
              {/*  className="operate"*/}
              {/*  src={rightRotation}*/}
              {/*  alt="rightRotation"*/}
              {/*  onClick={() => cropper.rotate(45)}*/}
              {/*/>*/}
              <span className='mr-6'>Shape</span>
          <div
            className={`operate ${!isCircleCropper ? "active" : ""}`}
            alt="rectangle"
            onClick={clickRectangleCropper}
          >
              <div style={{width:'16px',height:'16px',border:'1.5px solid var(--image-crop-shape-item-border-color)'}}></div>
          </div>
          <div
              style={{marginLeft:'8px'}}
            className={`operate ${isCircleCropper ? "active" : ""}`}
            alt="circle"
            onClick={clickCircleCropper}>
              <div style={{width:'16px',height:'16px',borderRadius:'8px', border:'1.5px solid var(--image-crop-shape-item-border-color)'}}></div>
          </div>
          {/*<img*/}
          {/*  className="operate"*/}
          {/*  src={reset}*/}
          {/*  alt="reset"*/}
          {/*  onClick={() => cropper.reset()}*/}
          {/*/>*/}
          {/*<img*/}
          {/*  className="operate"*/}
          {/*  src={download}*/}
          {/*  alt="download"*/}
          {/*  onClick={downloadImage}*/}
          {/*/>*/}
        </div>
      </>
    );
  }),
);

export default EditorBar;
